import { Form, Formik, FormikState } from 'formik';
import { useEffect, useState } from 'react';
import { RiCloseCircleLine } from 'react-icons/ri';
import * as Yup from 'yup';

import UserTable from '../UserTable';

import useUpdateGlobalNotificaiton from '@/hooks/globalNotification/useUpdateGlobalNotification';
import {
	IGlobalNotificationResponse,
	ITargetNotificationUser,
	IUpdateGlobalNotification,
} from '@/interfaces/entities/global-notification';
import { notificationService } from '@/services/notification.service';

import NotificationDatePicker from '@components/global-notification/NotificationDatePicker.tsx';

type IResetForm = (
	nextState?:
		| Partial<
				FormikState<{
					id: number;
					message: string;
					scheduledAt: Date;
					targetNotificationUsers: ITargetNotificationUser[];
					eventId: number;
					sent: boolean;
				}>
		  >
		| undefined,
) => void;

const EditGlobalNotificationModal = ({
	closeModal,
	initialState,
}: {
	closeModal: () => void;
	initialState: IGlobalNotificationResponse;
}) => {
	const updateGlobalNotification = useUpdateGlobalNotificaiton();

	const [selectedUsers, setSelectedUsers] = useState<number[]>();

	useEffect(() => {
		const usersSelected = initialState.targetNotificationUsers.map(
			(target) => target.user?.id,
		);
		const filteredUsersSelected: number[] = usersSelected.filter(
			(user): user is number => user !== undefined,
		);
		setSelectedUsers(filteredUsersSelected);
	}, [initialState.targetNotificationUsers]);

	const [loading, setLoading] = useState(false);
	const [usersError, setUsersError] = useState<string | null>(null);
	const [changeDate, setChangeDate] = useState(false);
	const [selectedDateTime, setSelectedDateTime] = useState('');
	const [allUsersAreSelected, setAllUsersAreSelected] = useState(false);
	const initialValues = {
		id: initialState.id,
		message: initialState.message,
		scheduledAt: initialState.scheduledAt,
		targetNotificationUsers: initialState.targetNotificationUsers,
		eventId: initialState.eventId,
		sent: initialState.sent,
	};

	const handleDateTimeChange = (date: Date | null) => {
		if (date) {
			setSelectedDateTime(date.toISOString());
			setChangeDate(true);
		}
	};
	const handleSelect = () => {
		return selectedDateTime ? new Date(selectedDateTime) : new Date();
	};

	const handleReset = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		resetForm: IResetForm,
	) => {
		e.preventDefault();
		e.stopPropagation();
		setLoading(true);
		resetForm();
		setSelectedDateTime('');
		setChangeDate(false);
		setLoading(false);
	};

	const handleSubmit = async (values: IGlobalNotificationResponse) => {
		setLoading(true);
		const updatedNotification: IUpdateGlobalNotification = {
			message: values.message,
			scheduledAt: changeDate ? new Date(selectedDateTime) : new Date(),
			targetNotificationUsersIds: selectedUsers || [],
		};
		try {
			updateGlobalNotification.mutate([values.id, updatedNotification]);
			notificationService.success('Notification updated successfully');
			closeModal();
		} catch (error) {
			console.error('Error editing notification:', error);
		} finally {
			setLoading(false);
		}
	};

	const validationSchema = Yup.object().shape({
		message: Yup.string().required('Message is required'),
	});

	return (
		<div
			data-test="edit-notification-modal"
			className="w-[100%] max-h-[90vh] overflow-y-auto"
		>
			<div className="flex justify-end pb-2">
				<button data-test="edit-notification-modal-close" onClick={closeModal}>
					<RiCloseCircleLine size={20} />
				</button>
			</div>

			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
			>
				{({ handleChange, handleBlur, values, errors, touched, resetForm }) => (
					<Form>
						<div className="flex-1 justify-center items-center bg-gray-100 p-4">
							<h1 className={`text-2xl text-center font-bold mt-2 mb-2 `}>
								Edit Global Notification
							</h1>
							<h2 className={`py-2 font-semibold`}>Message:</h2>
							<textarea
								data-test="edit-message-input"
								className={`w-full bg-white border border-gray-300 rounded-md p-2 resize-none overflow-y-auto`}
								placeholder="Message"
								onChange={handleChange}
								onBlur={handleBlur}
								value={values.message}
								name="message"
								rows={2}
							/>
							{touched.message && errors.message && (
								<p className={`text-red-500 text-sm`}>{errors.message}</p>
							)}
							<div className="flex justify-center items-center gap-8 my-2">
								<div className="flex items-center justify-center">
									<div className={`w-full flex items-center justify-center`}>
										<h2 className={`py-2 font-semibold`}>Date & Time</h2>
										<NotificationDatePicker
											selected={handleSelect()}
											onChange={handleDateTimeChange}
											minDate={new Date()}
										/>
									</div>
								</div>
								<div className="flex justify-center">
									<h2 className={`px-4 font-semibold`}>Scheduled at:</h2>
									{changeDate ? (
										<h3>{new Date(selectedDateTime).toLocaleString()}</h3>
									) : (
										<h3>Right now</h3>
									)}
								</div>
							</div>
							<div>
								<div className="h-full w-full flex flex-col items-center">
									<UserTable
										selectedUsers={selectedUsers ?? []}
										setSelectedUsers={setSelectedUsers}
										usersError={usersError}
										setUsersError={setUsersError}
										allUsersAreSelected={allUsersAreSelected}
										setAllUsersAreSelected={setAllUsersAreSelected}
									/>
								</div>
							</div>
							<div className="flex justify-center items-center w-full mt-4">
								<button
									data-test="edit-button"
									className="bg-blue-600 hover:bg-blue-500 items-center justify-center px-3 py-1 rounded-md mx-4 ml-2 w-[40%] disabled:opacity-50"
									type="submit"
									disabled={loading}
								>
									<p className={`text-white text-center`}>EDIT</p>
								</button>
								<button
									className="bg-red-600 hover:bg-red-500 items-center justify-center px-3 py-1 rounded-md my-2 disabled:opacity-50"
									onClick={(e) => handleReset(e, resetForm)}
									disabled={loading}
								>
									<p className="text-white text-center">RESET</p>
								</button>
							</div>
						</div>
					</Form>
				)}
			</Formik>
		</div>
	);
};

export default EditGlobalNotificationModal;
